import { LOCALES } from 'root-constants'

export const enum Cohort {
  STYLIO_SNRS2 = 'stylio_snrs2',
  STYLIO_SNRS = 'stylio_snrs',
  STYLIO_SNRS_IO = 'stylio_snrs_io',
  STYLIO_1 = 'stylio_1',
  STYLIO_PLUS = 'stylio_plus',
  STYLIO_CAPSULE2 = 'stylio_capsule2',
  STYLIO_CAPMOMS = 'stylio_capmoms',
  STYLIO_CAPMOMS4 = 'stylio_capmoms4',
  STYLIO_CAPSULE = 'stylio_capsule',
  STYLIO_CAPSULE3 = 'stylio_capsule3',
  STYLIO_CAPSULE_IO = 'stylio_capsule_io',
  STYLIO_CAPSEN = 'stylio_capsen',
  STYLIO_CMP = 'stylio_cmp',
  STYLIO_CMP2 = 'stylio_cmp2',
  STYLIO_ARCH = 'stylio_arch',
  STYLIO_ARCH_IO = 'stylio_arch_io',
  STYLIO_HUSAP = 'stylio_husap',
  STYLIO_CAPSULE_61 = 'stylio_capsule_61',
  STYLIO_CAPSULE_71 = 'stylio_capsule_71',
  STYLIO_CAPSULE_81 = 'stylio_capsule_81',
  STYLIO_ARCH_61 = 'stylio_arch_61',
  STYLIO_ARCH_71 = 'stylio_arch_71',
  STYLIO_ARCH_81 = 'stylio_arch_81',
  STYLIO_1_OLD = 'stylio_1_old',
}

export const FALLBACK_COHORT = Cohort.STYLIO_1_OLD

export const FULLY_NEW_SUBSCRIPTIONS_COHORTS: Cohort[] = [
  Cohort.STYLIO_SNRS2,
  Cohort.STYLIO_SNRS,
  Cohort.STYLIO_SNRS_IO,
  Cohort.STYLIO_CAPMOMS,
  Cohort.STYLIO_CAPSULE2,
  Cohort.STYLIO_CAPMOMS4,
  Cohort.STYLIO_CAPSULE,
  Cohort.STYLIO_CAPSULE3,
  Cohort.STYLIO_CAPSULE_IO,
  Cohort.STYLIO_CAPSEN,
  Cohort.STYLIO_CMP,
  Cohort.STYLIO_CMP2,
  Cohort.STYLIO_ARCH,
  Cohort.STYLIO_ARCH_IO,
  Cohort.STYLIO_HUSAP,
  Cohort.STYLIO_CAPSULE_61,
  Cohort.STYLIO_CAPSULE_71,
  Cohort.STYLIO_CAPSULE_81,
  Cohort.STYLIO_ARCH_61,
  Cohort.STYLIO_ARCH_71,
  Cohort.STYLIO_ARCH_81,
  Cohort.STYLIO_1,
  Cohort.STYLIO_PLUS,
]

export const EMAIL_PAYWALL_COHORTS = [
  Cohort.STYLIO_CAPSULE_61,
  Cohort.STYLIO_CAPSULE_71,
  Cohort.STYLIO_CAPSULE_81,
  Cohort.STYLIO_ARCH_61,
  Cohort.STYLIO_ARCH_71,
  Cohort.STYLIO_ARCH_81,
] as const

export const isEmailPaywallCohort = (
  cohort?: string | null,
): cohort is typeof EMAIL_PAYWALL_COHORTS[number] =>
  !!cohort && (EMAIL_PAYWALL_COHORTS as readonly string[]).includes(cohort)

export const LOCALIZED_COHORT_LOCALES = [
  LOCALES.EN,
  LOCALES.ES,
  LOCALES.FR,
  LOCALES.PT,
  LOCALES.DE,
  LOCALES.IT,
] as const

export const SUPPORTED_LOCALES_BY_COHORT: Partial<Record<
  Cohort,
  readonly string[]
>> = {
  [Cohort.STYLIO_ARCH]: LOCALIZED_COHORT_LOCALES,
  [Cohort.STYLIO_ARCH_IO]: LOCALIZED_COHORT_LOCALES,
  [Cohort.STYLIO_CAPSULE]: LOCALIZED_COHORT_LOCALES,
  [Cohort.STYLIO_CAPSULE_IO]: LOCALIZED_COHORT_LOCALES,
}

export const getSupportedLocalesForCohort = (
  cohort?: string | null,
): readonly string[] =>
  (cohort && SUPPORTED_LOCALES_BY_COHORT[cohort as Cohort]) || [LOCALES.EN]

export const DISCOUNT_LEVEL_BY_COHORT: Record<
  typeof EMAIL_PAYWALL_COHORTS[number],
  61 | 71 | 81
> = {
  [Cohort.STYLIO_CAPSULE_61]: 61,
  [Cohort.STYLIO_CAPSULE_71]: 71,
  [Cohort.STYLIO_CAPSULE_81]: 81,
  [Cohort.STYLIO_ARCH_61]: 61,
  [Cohort.STYLIO_ARCH_71]: 71,
  [Cohort.STYLIO_ARCH_81]: 81,
}

export const getDiscountLevel = (
  cohort?: string | null,
): 61 | 71 | 81 | undefined => {
  if (!isEmailPaywallCohort(cohort)) return undefined

  return DISCOUNT_LEVEL_BY_COHORT[cohort]
}

export const enum PaymentSystem {
  PAYPAL = 'paypal',
  STRIPE = 'stripe',
}

export const enum Currency {
  USD = 'USD',
}

export const CURRENCY_SIGN_MAP = {
  [Currency.USD]: '$',
}

export const enum ProcessingKey {
  FETCH_PLANS = 'FETCH_PLANS',
  CREATE_APPWRITE_PROFILE = 'CREATE_APPWRITE_PROFILE',
  GET_FIRST_LEAD = 'GET_FIRST_LEAD',
  FETCH_LEAD = 'FETCH_LEAD',
  UPDATE_LEAD_WITH_EMAIL = 'UPDATE_LEAD_WITH_EMAIL',
  CREATE_LEAD = 'CREATE_LEAD',
  CREATE_COHORT_LEAD = 'CREATE_COHORT_LEAD',
  FETCH_BREEDS = 'FETCH_BREEDS',
  FETCH_COMMANDS = 'FETCH_COMMANDS',
  CREATE_USER_ACCOUNT = 'CREATE_USER_ACCOUNT',
  SAVE_USER_ANSWER = 'SAVE_USER_ANSWER',
  CREATE_FIREBASE_USER_WITH_EMAIL_AND_PASSWORD = 'CREATE_FIREBASE_USER_WITH_EMAIL_AND_PASSWORD',
  PURCHASE_PAYPAL = 'PURCHASE_PAYPAL',
  PURCHASE_STRIPE = 'PURCHASE_STRIPE',
  PURCHASE_PAYPAL_PENDING = 'PURCHASE_PAYPAL_PENDING',
  PURCHASE_STRIPE_PENDING = 'PURCHASE_STRIPE_PENDING',
  PURCHASE_UPSELL = 'PURCHASE_UPSELL',
  PURCHASE_UPSELL_PENDING = 'PURCHASE_UPSELL_PENDING',
  PURCHASE_CHAT_UPSELL = 'PURCHASE_CHAT_UPSELL',
  PURCHASE_CHAT_UPSELL_PENDING = 'PURCHASE_CHAT_UPSELL_PENDING',
  FETCH_PREDICTED_LTV = 'FETCH_PREDICTED_LTV',
  FETCH_GEO_DATA = 'FETCH_GEO_DATA',
  FETCH_CREATIVE = 'FETCH_CREATIVE',
  FETCH_COLOR_ANALYSIS = 'FETCH_COLOR_ANALYSIS',
}

export const enum GlobalError {
  COMMON_ERROR = 'something went wrong',
  USER_HAS_NEW_SUBSCRIPTION_ERROR = 'error during the purchase process',
  USER_HAS_EXPIRED_TOKEN_ERROR = 'user has expired token',
  COMMON_PURCHASE_ERROR = 'something went wrong in purchase',
  PURCHASE_PLAN_RESPONSE_ERROR = 'something went wrong in PurchasePlanResponse',
  PURCHASE_UPSELL_RESPONSE_ERROR = 'something went wrong in PurchaseUpsellResponse',
  PURCHASE_CHAT_UPSELL_RESPONSE_ERROR = 'something went wrong in PurchaseChatUpsellResponse',
}

export const enum InputValidationText {
  EMAIL_EXISTS = 'account.accountAlreadyExist',
  INVALID_EMAIL = 'onboarding.email.validation',
  INVALID_EMAIL_VARIANT_2 = 'Please, check email address. It looks like there might be a typo',
  EMPTY_EMAIL_ERROR = 'Please, provide your email that we can send you the training plan',
  SHORT_PASSWORD = 'account.shortPassword',
  WRONG_CONFIRMATION_PASSWORD = 'account.notEqualPassword',
  WRONG_PASSWORD = 'The password you entered is incorrect',
  WRONG_EMAIL = 'The email you entered is incorrect',
  INVALID_TEXT = `Your input doesn't look valid. Please, check`,
}

export const EMAIL_DOMAINS = [
  '@gmail.com',
  '@hotmail.com',
  '@yahoo.com',
  '@outlook.com',
  '@aol.com',
  '@icloud.com',
  '@live.com',
  '@msn.com',
  '@me.com',
  '@mac.com',
  '@googlemail.com',
  '@facebook.com',
  '@verizon.net',
  '@rediffmail.com',
  '@zohomail.com',
  '@zoho.com',
  '@mail.com',
]

export const enum HeaderId {
  TITLE_MENU = 'title_menu',
  BACK_TITLE = 'back_title',
  BACK_TITLE_MENU = 'back_title_menu',
  BACK_PROGRESS_MENU = 'back_progress_menu',
  POLICY = 'policy',
}

export const POLICY_PORTAL_HEADER_CLASS = 'policy-portal-header'

export const OPTIMIZED_VARIANT_ID = 'no_test'

export const MANAGE_SUBSCRIPTION_LINK =
  'https://account.nove8.com/stylio/refund-contact-us'

export const COUNTRIES_WITH_MANAGE_SUBSCRIPTION_BUTTON = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'NO',
  'IS',
  'CH',
  'LI',
]
